import { SearchConfig } from "lib/api/types";
import React, { useEffect, useMemo } from "react";
import { FinderState } from "./finderReducer";
import {
	useFlexLayout,
	useResizeColumns,
	useRowSelect,
	useTable,
} from "react-table";
import PaginationButton from "components/PaginationButton";
import jsonpointer from "lib/jsonpointer/jsonpointer";
import { useTranslation } from "react-i18next";
import ViewDropdown from "components/CustomViews/ViewDropdown";

type Props = {
	onLoadMore: () => void;
	hasMore: boolean;
	results: FinderState["results"];
	columns: SearchConfig["columns"];
	limit?: number;
	addSelected: (ids: string[]) => void;
	loadingPage: boolean;
	selected: string[];
	hideSelectBoxes?: boolean;
	isMobile?: boolean;
	setFormAction?: React.Dispatch<
		React.SetStateAction<{
			type: "VIEW" | "EDIT" | "CREATE";
			index: number;
		} | null>
	>;
	showEdit?: boolean;
	showView?: boolean;
};

function SearchResults({
	onLoadMore,
	hasMore,
	results,
	columns,
	limit,
	loadingPage,
	selected,
	addSelected,
	hideSelectBoxes,
	isMobile,
	setFormAction,
	showEdit,
	showView,
}: Props) {
	const { t } = useTranslation();

	// Initial row selected values
	const selectedRowIds = useMemo(() => {
		const selectedIds = selected.map(id => {
			return { [id]: true };
		});
		return Object.assign({}, ...selectedIds);
	}, [selected]);

	// Setting up the table data by using results array
	const tableData = useMemo(() => {
		return results?.map(item => {
			const props = columns?.map(({ id }) => {
				let value;

				if (jsonpointer.has(item.data, id)) {
					const val = jsonpointer.get(item.data, id);
					if (val !== undefined) {
						if (Array.isArray(val)) {
							value = val.join(", ");
						} else if (typeof val === "boolean") {
							value = val.toString();
						} else {
							value = val;
						}
					}
				}

				return { [id]: value };
			});
			return { ...Object.assign({}, ...props) };
		});
	}, [columns, results]);

	// Setting up the column names
	const tableColumns = useMemo(
		() =>
			columns?.map(({ id, label }) => {
				return {
					Header: label,
					accessor: id,
					minWidth: 100,
					maxWidth: 350,
					width: 150,
				};
			}),
		[columns]
	);

	// Create the table using react-table by passing in the column
	// and data array created above
	// useRowSelect hook to create selection column in table
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		selectedFlatRows,
		toggleAllRowsSelected,
	} = useTable(
		{
			columns: tableColumns ? tableColumns : [],
			data: tableData ? tableData : [],
			initialState: {
				selectedRowIds,
			},
		},
		useResizeColumns,
		useFlexLayout,
		useRowSelect,
		hooks => {
			if (hideSelectBoxes && (showEdit || showView))
				hooks.visibleColumns.push(columns => [
					{
						id: "selection",
						width: 40,
						disableResizing: true,
					},

					...columns,
					{
						id: "actions",
						accessor: "actions",
						width: 50,
						minWidth: 50,
						disableResizing: true,
						sticky: "right",
						Cell: ({ row }: { row: { index: number } }) => {
							return (
								<ViewDropdown
									row={row}
									disabled={false}
									setFormAction={setFormAction}
									showEdit={showEdit}
									showView={showView}
								/>
							);
						},
					},
				]);
			else if (hideSelectBoxes)
				hooks.visibleColumns.push(columns => [
					{
						id: "selection",
						width: 40,
						disableResizing: true,
					},

					...columns,
				]);
			else
				hooks.visibleColumns.push(columns => [
					{
						id: "selection",
						width: 40,
						disableResizing: true,
						Header: ({ getToggleAllRowsSelectedProps }) => {
							// Toggle all checkbox will show if the limit is undefined
							const { indeterminate, ...rest } =
								getToggleAllRowsSelectedProps();
							return (
								<>
									{!limit ? (
										<div className="form-check custom-checkbox">
											<input
												id={"all"}
												className="form-check-input"
												type="checkbox"
												{...rest}
											/>
											<label
												htmlFor={"all"}
												className="form-check-label"
											/>
										</div>
									) : null}
								</>
							);
						},
						Cell: ({ row, toggleAllRowsSelected, state }) => {
							// Checkbox will be shown if limit is undefined not equal to 1,
							// otherwise a radio will be shown
							const { indeterminate, ...rest } =
								row.getToggleRowSelectedProps();
							return (
								<>
									{!limit || limit !== 1 ? (
										<div className="form-check custom-checkbox">
											<input
												id={row.id}
												className="form-check-input"
												type="checkbox"
												// If num of records selected equals limit and record isn't
												// selected then disable the checkbox
												disabled={
													Object.keys(
														state.selectedRowIds
													).length === limit &&
													!row.isSelected
												}
												{...rest}
											/>
											<label
												htmlFor={row.id}
												className="form-check-label"
											/>
										</div>
									) : (
										<div className="form-check custom-radio">
											<input
												id={row.id}
												className="form-check-input"
												type="radio"
												{...rest}
												onChange={() => {
													toggleAllRowsSelected(
														false
													);
													row.toggleRowSelected(true);
												}}
											/>
											<label
												htmlFor={row.id}
												className="form-check-label"
												aria-label={row.id}
											/>
										</div>
									)}
								</>
							);
						},
					},
					...columns,
				]);
		}
	);

	// When there is a change in selectedFlatRows we should update the selectedIds
	useEffect(() => {
		if (results && !hideSelectBoxes) {
			const selectedIds = selectedFlatRows.map(row => row.id);
			addSelected(selectedIds);
		}
	}, [addSelected, hideSelectBoxes, results, selectedFlatRows]);

	return results && results.length > 0 ? (
		<div className="finder__search-results-wrapper">
			<table
				{...getTableProps({ style: { minWidth: "100%" } })}
				className="finder__search-results table">
				{!isMobile && (
					<thead className="finder__search-results-thead">
						{headerGroups.map(headerGroup => (
							<tr {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map(column => (
									<th {...column.getHeaderProps()}>
										{column.render("Header")}
										{column.canResize && (
											<div
												{...column.getResizerProps()}
												className={`resizer ${
													column.isResizing
														? "isResizing"
														: ""
												}`}
											/>
										)}
									</th>
								))}
							</tr>
						))}
					</thead>
				)}
				<tbody {...getTableBodyProps()}>
					{rows.map(row => {
						prepareRow(row);
						return (
							<tr
								{...row.getRowProps({
									style: {
										width: "100%",
										minWidth: "100%",
									},
								})}
								className={`finder__search-results-tr ${
									row.isSelected ? "table-active" : ""
								}`}
								onClick={() => {
									if (!limit || limit !== 1) {
										if (row.isSelected) {
											row.toggleRowSelected(false);
										} else {
											row.toggleRowSelected(true);
										}
									} else {
										toggleAllRowsSelected(false);
										row.toggleRowSelected(true);
									}
								}}>
								{row.cells.map(cell => {
									return (
										<td
											{...cell.getCellProps()}
											className={`finder__search-results-td ${
												cell.column.id === "actions"
													? "customView-action"
													: ""
											}`}>
											<div className="finder__search-results-chead">
												{typeof cell.column.Header ===
												"string"
													? cell.column.Header
													: ""}
											</div>
											<div
												className={`${
													cell.column.id ===
													"selection"
														? "finder__search-results-select"
														: "finder__search-results-ccell"
												}`}>
												{cell.render("Cell")}
											</div>
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
			<div className="finder__pagination-btn-container">
				<PaginationButton
					pageType="finder__search-results"
					loading={loadingPage}
					page={onLoadMore}
					hasMore={hasMore}
				/>
			</div>
		</div>
	) : (
		<div
			className="d-flex justify-content-center m-5"
			style={hideSelectBoxes ? { alignItems: "center" } : undefined}>
			{t("elements.finder.noRecordsMatched")}
		</div>
	);
}

export default SearchResults;
